import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    IconButton,
    styled,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab,
    Chip,
    Divider,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Collapse,
    Tooltip,
    Badge,
    Button,
    CircularProgress
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { SinavDetayResponseModal, SinavSoruKazanim } from '../types/modals';
import { Quiz } from '@mui/icons-material';

// AI Service interface
interface ChatResponse {
    task_id: string;
    status: string;
}
interface Message {
    role: string;
    content: string;
}
interface ResultResponse {
    model: string;
    messages: Message[];
    chat_id: string;
    session_id: string;
    id: string;
}

const PdfReaderBaseUrl = "https://proxy.imsmsoftware.com/";

// AI Service
const aiService = {
    async chat(message: string): Promise<ChatResponse | null> {
        try {
            const formData = new FormData();
            formData.append('email', 'omer@masraff.co');
            formData.append('password', 'XzjTcevzorF@v4%sUfxs855k');
            formData.append('message', message);
            const response = await fetch(PdfReaderBaseUrl + 'api/chat', {
                method: 'POST',
                body: formData
            });
            if (!response.ok) {
                console.error('API response not successful:', response.status, response.statusText);
                return null;
            }
            try {
                return await response.json();
            } catch (error) {
                console.error('Error parsing JSON response:', error);
                return null;
            }
        } catch (error) {
            console.error('Error in chat request:', error);
            return null;
        }
    },
    async getResult(taskId: string): Promise<ResultResponse | null> {
        try {
            const response = await fetch(PdfReaderBaseUrl + `api/result/${taskId}`);
            if (!response.ok) {
                console.error('API response not successful:', response.status, response.statusText);
                return null;
            }
            try {
                return await response.json();
            } catch (error) {
                console.error('Error parsing JSON response:', error);
                return null;
            }
        } catch (error) {
            console.error('Error in result request:', error);
            return null;
        }
    }
};

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    padding: '12px 16px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
        transition: 'background-color 0.2s',
    },
}));

const InfoCard = styled(Card)(({ theme }) => ({
    height: '100%',
    boxShadow: theme.shadows[2],
    transition: 'box-shadow 0.3s',
    '&:hover': {
        boxShadow: theme.shadows[6],
    },
}));

const TestHeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    cursor: 'pointer',
}));

const TestResultContainer = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius * 1.5,
    transition: 'transform 0.2s',
    '&:hover': {
        boxShadow: theme.shadows[4],
    },
}));

const TabPanel = (props: { children?: React.ReactNode; value: number; index: number }) => {
    const { children, value, index } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`}>
            {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
        </div>
    );
};

interface SinavDetayModalProps {
    open: boolean;
    onClose: () => void;
    sinavDetay: SinavDetayResponseModal;
}

export const SinavDetayModal: React.FC<SinavDetayModalProps> = ({ open, onClose, sinavDetay }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [tabValue, setTabValue] = useState(0);
    const [expandedTests, setExpandedTests] = useState<{ [key: string]: boolean }>({});
    const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
    const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
    const [hasRequestedAnalysis, setHasRequestedAnalysis] = useState(false);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const toggleTestExpand = (testName: string) => {
        setExpandedTests(prev => ({
            ...prev,
            [testName]: !prev[testName]
        }));
    };

    // Improved test name normalization
    const normalizeTestName = (testName: string): string => {
        return testName.trim().toUpperCase()
            .replace(/\s*-\s*/g, '-')
            .replace(/\s+/g, ' ')
            .replace(/BIL(IMLER)?/, 'BİLGİLER')
            .replace(/EDBYT/, 'EDEBİYAT')
            .replace(/SOSY/, 'SOSYAL')
            .replace(/FEN B/, 'FEN')
            .replace(/İNK\.TAR/, 'İNKILAP TARİHİ')
            .replace(/DIN K/, 'DİN KÜLTÜRÜ')
            .replace(/TÜRKÇE/, 'TÜRKÇE')
            .replace(/MATEMAT[İI]K/, 'MATEMATİK')
            .replace(/YABANCI D[İI]L/, 'YABANCI DİL');
    };

    // Group tests by their main test (memoized for performance)
    const groupedTests = useMemo(() => {
        return sinavDetay.sinavDetayTestList.reduce((acc, test) => {
            if (!test.anaTestAdi) {
                if (!acc[test.testAdi]) {
                    acc[test.testAdi] = {
                        main: test,
                        subTests: []
                    };
                } else {
                    acc[test.testAdi].main = test;
                }
            } else {
                if (!acc[test.anaTestAdi]) {
                    acc[test.anaTestAdi] = {
                        main: null,
                        subTests: [test]
                    };
                } else {
                    acc[test.anaTestAdi].subTests.push(test);
                }
            }
            return acc;
        }, {} as { [key: string]: { main: any, subTests: any[] } });
    }, [sinavDetay.sinavDetayTestList]);

    // Group questions by test and subject (memoized for performance)
    const groupedQuestions = useMemo(() => {
        return sinavDetay.sinavSoruKazanimList?.reduce((acc, question) => {
            const key = `${question.testAdi}-${question.dersAdi || ''}`;
            if (!acc[key]) {
                acc[key] = {
                    testAdi: question.testAdi,
                    dersAdi: question.dersAdi || '',
                    testNo: question.testNo,
                    dersNo: question.dersNo,
                    questions: []
                };
            }
            acc[key].questions.push(question);
            return acc;
        }, {} as { [key: string]: { testAdi: string; dersAdi: string; testNo: number; dersNo: number; questions: SinavSoruKazanim[] } });
    }, [sinavDetay.sinavSoruKazanimList]);

    // Function to find the matching test in sinavDetayTestList for a given question
    const findMatchingTest = (questionTestName: string): any => {
        const normalizedQuestionTestName = normalizeTestName(questionTestName);

        return sinavDetay.sinavDetayTestList.find(test => {
            const normalizedTestName = normalizeTestName(test.testAdi);

            // First try exact match
            if (normalizedTestName === normalizedQuestionTestName) {
                return true;
            }

            // Try partial match
            if (normalizedTestName.includes(normalizedQuestionTestName) ||
                normalizedQuestionTestName.includes(normalizedTestName)) {
                return true;
            }

            // Handle special cases
            if (normalizedQuestionTestName.includes('İNK.TAR') && normalizedTestName.includes('SOSYAL')) {
                return true;
            }

            if (normalizedTestName.includes('FEN')) {
                return true;
            }

            if (normalizedQuestionTestName.includes('DİN KÜLT') && normalizedTestName.includes('DİN')) {
                return true;
            }

            return false;
        });
    };

    // Function to determine if a question is answered correctly
    const isQuestionCorrect = (testAdi: string, soruNo: number): boolean | null | 'cancelled' => {
        const test = findMatchingTest(testAdi);
        if (!test || !test.ogrenciCevap || !test.cevapAnahtari) return null;

        // Adjust for 0-based index
        const index = soruNo - 1;
        if (index < 0 || index >= test.ogrenciCevap.length) return null;

        const ogrenciCevap = test.ogrenciCevap.charAt(index);
        const dogruCevap = test.cevapAnahtari.charAt(index);

        // İptal edilen soru kontrolü
        if (dogruCevap === '.') return 'cancelled';

        // If student didn't answer
        if (!ogrenciCevap || ogrenciCevap === ' ') return null;

        return ogrenciCevap === dogruCevap;
    };

    // Calculate summary statistics for each subject group
    const calculateGroupStats = (group: { testAdi: string; dersAdi: string; questions: SinavSoruKazanim[] }) => {
        const total = group.questions.length;
        let correct = 0;
        let incorrect = 0;
        let unanswered = 0;

        group.questions.forEach(question => {
            const result = isQuestionCorrect(group.testAdi, question.soruNo);
            if (result === true) correct++;
            else if (result === false) incorrect++;
            else unanswered++;
        });

        return { total, correct, incorrect, unanswered };
    };

    // Function to prepare data for AI analysis
    const prepareDataForAI = () => {
        let message = `Aşağıda deneme sınavına ait soruların konu kazanımlarını ve doğru/yanlış durumlarını vereceğim. Bunları inceleyerek sınavdaki genel durumumu, ağırlık vermem gereken konuları vs. dosthane bir şekilde emojilerle söyle.Aldığım puana göre geçen seneki Türkiede yapılan tyt - ayt sıralamamı söyle. \n\n`;

        message += `Sınav Adı: ${sinavDetay.sinavAdi}\n`;
        message += `Öğrenci: ${sinavDetay.ogrenciAdi}\n`;
        message += `Sınıf: ${sinavDetay.sinifi}\n\n`;

        // Add test results
        message += `GENEL SONUÇLAR:\n`;
        sinavDetay.sinavDetayTestList.filter(test => !test.anaTestAdi && test.soru > 0).forEach(test => {
            message += `${test.testAdi}: ${test.dogru} Doğru, ${test.yanlis} Yanlış, ${test.net.toFixed(2)} Net\n`;
        });

        message += `\nKONU BAZLI SONUÇLAR:\n`;

        // Add question results by subject
        Object.values(groupedQuestions || {}).forEach(group => {
            const stats = calculateGroupStats(group);
            message += `\n${group.testAdi} ${group.dersAdi ? `- ${group.dersAdi}` : ''}: ${stats.correct} Doğru, ${stats.incorrect} Yanlış, ${stats.unanswered} Boş\n`;

            group.questions.sort((a, b) => a.soruNo - b.soruNo).forEach(question => {
                const result = isQuestionCorrect(group.testAdi, question.soruNo);
                const status = result === true ? "DOĞRU" : result === false ? "YANLIŞ" : "BOŞ";
                message += `Soru ${question.soruNo} (${status}): ${question.konu}\n`;
            });
        });

        return message;
    };

    // Improved AI analysis request with better error handling
    const requestAiAnalysis = async () => {
        if (isLoadingAnalysis) return;

        setIsLoadingAnalysis(true);
        setHasRequestedAnalysis(true);

        try {
            const message = prepareDataForAI();
            const chatResponse = await aiService.chat(message);

            if (!chatResponse || !chatResponse.task_id) {
                throw new Error("Analiz başlatılamadı");
            }

            // Poll for results with timeout
            let resultResponse: ResultResponse | null = null;
            let attempts = 0;
            const maxAttempts = 30; // 30 seconds timeout

            while (!resultResponse && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
                try {
                    resultResponse = await aiService.getResult(chatResponse.task_id);
                    if (resultResponse?.messages?.length && resultResponse?.messages?.length > 0) {
                        break;
                    }
                } catch (error) {
                    console.error("Error polling for results:", error);
                }
                attempts++;
            }

            if (resultResponse) {
                const assistantMessage = resultResponse.messages.find(m => m.role === "assistant");
                setAiAnalysis(assistantMessage?.content || "Analiz sonuçları alınamadı.");
            } else {
                setAiAnalysis("Analiz zaman aşımına uğradı. Lütfen daha sonra tekrar deneyin.");
            }
        } catch (error) {
            console.error("Error in AI analysis:", error);
            setAiAnalysis(`Analiz sırasında bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
        } finally {
            setIsLoadingAnalysis(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : theme.shape.borderRadius * 2,
                    minHeight: isMobile ? '100vh' : '80vh',
                    maxHeight: isMobile ? '100vh' : '90vh',
                    bgcolor: '#ffffff',
                    overflowY: 'auto'
                }
            }}
        >
            <DialogTitle
                sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    m: 0,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box display="flex" alignItems="center">
                    <AssignmentIcon sx={{ mr: 1 }} />
                    <Typography variant="h6" component="span">
                        {sinavDetay.sinavAdi}
                    </Typography>
                    <Chip
                        label={new Date(sinavDetay.sinavTarihi).toLocaleDateString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        })}
                        size="small"
                        sx={{ ml: 2, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    />
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{ color: 'white' }}
                    aria-label="Kapat"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: theme.palette.background.paper }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant={isMobile ? "fullWidth" : "standard"}
                    centered={!isMobile}
                    indicatorColor="primary"
                    textColor="primary"
                    aria-label="sınav detay sekmeleri"
                >
                    <Tab
                        icon={<PersonIcon />}
                        label="Öğrenci Bilgileri"
                        iconPosition="start"
                        sx={{ minHeight: 64 }}
                    />
                    <Tab
                        icon={<EqualizerIcon />}
                        label="Test Sonuçları"
                        iconPosition="start"
                        sx={{ minHeight: 64 }}
                    />
                    <Tab
                        icon={<FormatListNumberedIcon />}
                        label="Cevap Anahtarı"
                        iconPosition="start"
                        sx={{ minHeight: 64 }}
                    />
                    {sinavDetay.sinavSoruKazanimList?.length > 0 && (
                        <Tab
                            icon={<Quiz />}
                            label="Soru Kazanımları"
                            iconPosition="start"
                            sx={{ minHeight: 64 }}
                        />
                    )}
                    <Tab
                        icon={<PsychologyIcon />}
                        label="Değerlendirme"
                        iconPosition="start"
                        sx={{ minHeight: 64 }}
                    />
                </Tabs>
            </Box>
            <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
                {/* Öğrenci Bilgileri Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <InfoCard>
                                <CardHeader
                                    avatar={
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                            <PersonIcon />
                                        </Avatar>
                                    }
                                    title="Öğrenci Bilgileri"
                                />
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {sinavDetay.ogrenciAdi}
                                    </Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Öğrenci No
                                            </Typography>
                                            <Typography variant="body1">
                                                {sinavDetay.ogrenciNo || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Sınıf
                                            </Typography>
                                            <Typography variant="body1">
                                                {sinavDetay.sinifi}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </InfoCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoCard>
                                <CardHeader
                                    avatar={
                                        <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                                            <SchoolIcon />
                                        </Avatar>
                                    }
                                    title="Okul Bilgileri"
                                />
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {sinavDetay.okulKodu}
                                    </Typography>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Sınav Tarihi
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(sinavDetay.sinavTarihi).toLocaleDateString('tr-TR', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric',
                                            weekday: 'long'
                                        })}
                                    </Typography>
                                </CardContent>
                            </InfoCard>
                        </Grid>
                        <Grid item xs={12}>
                            <InfoCard>
                                <CardHeader
                                    avatar={
                                        <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                                            <EqualizerIcon />
                                        </Avatar>
                                    }
                                    title="Puan Bilgileri"
                                />
                                <CardContent>
                                    <TableContainer component={Paper} elevation={0}>
                                        <Table size={isMobile ? "small" : "medium"}>
                                            <TableHead>
                                                <TableRow>
                                                    <StyledTableCell>Puan Türü</StyledTableCell>
                                                    <StyledTableCell align="center">Öğrenci Puanı</StyledTableCell>
                                                    <StyledTableCell align="center">Sınıf Sıra</StyledTableCell>
                                                    <StyledTableCell align="center">Genel Sıra</StyledTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sinavDetay.sinavDetayPuanList.map((puan, index) => (
                                                    <StyledTableRow key={index}>
                                                        <TableCell>
                                                            <Typography variant="subtitle2">
                                                                {puan.puan}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={puan.ogrenciPuani.toFixed(3)}
                                                                color="primary"
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={puan.siniftakiSirasi}
                                                                color="secondary"
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={puan.genelSirasi}
                                                                color="default"
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </StyledTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </InfoCard>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Test Sonuçları Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Test Sonuçları
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Aşağıda sınavdaki testlere ait sonuçlar listelenmiştir. Alt testleri görüntülemek için satıra tıklayabilirsiniz.
                        </Typography>
                    </Box>

                    {/* Test sonuçları listesi */}
                    {sinavDetay.sinavDetayTestList
                        .filter(test => !test.anaTestAdi)
                        .map((test, index) => (
                            <TestResultContainer key={index} elevation={2}>
                                <TestHeaderBox onClick={() => toggleTestExpand(test.testAdi)}>
                                    <Box display="flex" alignItems="center">
                                        <AssignmentIcon sx={{ mr: 1.5 }} />
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {test.testAdi}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                            <Badge
                                                badgeContent={test.dogru}
                                                color="success"
                                                sx={{ mr: 1.5 }}
                                                max={999}
                                            >
                                                <CheckCircleIcon />
                                            </Badge>
                                            <Badge
                                                badgeContent={test.yanlis}
                                                color="error"
                                                sx={{ mr: 1.5 }}
                                                max={999}
                                            >
                                                <CancelIcon />
                                            </Badge>
                                            <Chip
                                                label={`Net: ${test.net.toFixed(2)}`}
                                                size="small"
                                                color="default"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </Box>
                                        {expandedTests[test.testAdi] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </Box>
                                </TestHeaderBox>

                                <Collapse
                                    in={expandedTests[test.testAdi] || false}
                                    timeout={300}
                                >
                                    <TableContainer>
                                        <Table size={isMobile ? "small" : "medium"}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Alt Test</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Soru</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Doğru</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Yanlış</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Net</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <StyledTableRow>
                                                    <TableCell>{test.testAdi}</TableCell>
                                                    <TableCell align="center">{test.soru}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={test.dogru}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={test.yanlis}
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={test.net.toFixed(2)}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                </StyledTableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Collapse>
                            </TestResultContainer>
                        ))}
                </TabPanel>

                {/* Cevap Anahtarı Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Cevap Anahtarı
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Aşağıda sınavdaki sorulara verilen cevaplar ve doğru cevaplar listelenmiştir. Doğru cevaplar yeşil, yanlış cevaplar kırmızı, boş bırakılan sorular turuncu, iptal edilen sorular ise mavi renk ile gösterilmiştir.
                        </Typography>
                    </Box>
                    <Grid container spacing={2}>
                        {sinavDetay.sinavDetayTestList
                            .filter(test => test.ogrenciCevap)
                            .map((test, index) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            height: '100%',
                                            borderRadius: theme.shape.borderRadius * 1.5,
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: theme.shadows[8]
                                            }
                                        }}
                                    >
                                        <CardHeader
                                            title={test.testAdi}
                                            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                                            sx={{
                                                bgcolor: theme.palette.primary.main,
                                                color: 'white',
                                                py: 1.5
                                            }}
                                        />
                                        <CardContent>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Öğrenci Cevapları
                                                    </Typography>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                        maxWidth: '100%'
                                                    }}>
                                                        {test.ogrenciCevap.split('').map((cevap, i) => {
                                                            const dogruCevap = test.cevapAnahtari.charAt(i);
                                                            const isCancelled = dogruCevap === '.'; // İptal edilen soru
                                                            const isEmpty = cevap === ' '; // Boş bırakılan soru
                                                            // İptal edilen sorular her durumda doğru sayılır
                                                            const isCorrect = isCancelled ? true : cevap === dogruCevap;

                                                            return (
                                                                <Tooltip
                                                                    key={i}
                                                                    title={`Soru ${i + 1}: ${isCancelled ? 'İptal Edildi' : isEmpty ? 'Boş' : isCorrect ? 'Doğru' : 'Yanlış'}`}
                                                                    arrow
                                                                >
                                                                    <Chip
                                                                        label={`${i + 1}: ${isEmpty ? '-' : cevap}`}
                                                                        size="small"
                                                                        color={
                                                                            isCancelled ? "info" :
                                                                                isEmpty ? "warning" :
                                                                                    isCorrect ? "success" :
                                                                                        "error"
                                                                        }
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            fontSize: '0.7rem'
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                                <Divider />
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Doğru Cevaplar
                                                    </Typography>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                        maxWidth: '100%'
                                                    }}>
                                                        {test.cevapAnahtari.split('').map((cevap, i) => {
                                                            const ogrenciCevap = test.ogrenciCevap.charAt(i);
                                                            const isCancelled = cevap === '.'; // İptal edilen soru
                                                            const isEmpty = ogrenciCevap === ' '; // Boş bırakılan soru
                                                            const isCorrect = isCancelled ? true : ogrenciCevap === cevap;

                                                            return (
                                                                <Tooltip
                                                                    key={i}
                                                                    title={`Soru ${i + 1}: ${isCancelled ? 'İptal Edildi - Tüm Cevaplar Doğru' : cevap}`}
                                                                    arrow
                                                                >
                                                                    <Chip
                                                                        label={`${i + 1}: ${isCancelled ? 'İPTAL' : cevap}`}
                                                                        size="small"
                                                                        color={
                                                                            isCancelled ? "info" :
                                                                                isEmpty ? "warning" :
                                                                                    isCorrect ? "success" :
                                                                                        "error"
                                                                        }
                                                                        variant="outlined"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            fontSize: '0.7rem',
                                                                            borderWidth: 2
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>

                                                {/* Renk Açıklamaları */}
                                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        label="Doğru"
                                                        sx={{ fontSize: '0.65rem' }}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        color="error"
                                                        label="Yanlış"
                                                        sx={{ fontSize: '0.65rem' }}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        label="Boş"
                                                        sx={{ fontSize: '0.65rem' }}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        color="info"
                                                        label="İptal"
                                                        sx={{ fontSize: '0.65rem' }}
                                                    />
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                    </Grid>
                </TabPanel>

                {/* Soru Kazanımları Tab - Enhanced with correctness indicators */}
                {sinavDetay.sinavSoruKazanimList?.length > 0 && (
                    <TabPanel value={tabValue} index={3}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Soru Kazanımları
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Aşağıda sınavdaki soruların ders ve konu bazında dağılımı ve doğru/yanlış durumları listelenmiştir.
                            </Typography>
                        </Box>

                        {/* Group questions by test */}
                        {Object.entries(groupedQuestions || {}).map(([key, group], index) => {
                            const stats = calculateGroupStats(group);
                            // Find matching test for color/style consistency
                            const matchingTest = findMatchingTest(group.testAdi);

                            return (
                                <Paper
                                    key={index}
                                    elevation={3}
                                    sx={{
                                        mb: 3,
                                        overflow: 'hidden',
                                        borderRadius: theme.shape.borderRadius * 1.5
                                    }}
                                >
                                    <Box sx={{
                                        bgcolor: theme.palette.primary.main,
                                        color: 'white',
                                        p: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap'
                                    }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {group.testAdi} {group.dersAdi ? `- ${group.dersAdi}` : ''}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, sm: 0 } }}>
                                            <Chip
                                                label={`Toplam: ${stats.total}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                            <Chip
                                                label={`Doğru: ${stats.correct}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: theme.palette.success.main,
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                            <Chip
                                                label={`Yanlış: ${stats.incorrect}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: theme.palette.error.main,
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                            {stats.unanswered > 0 && (
                                                <Chip
                                                    label={`Boş: ${stats.unanswered}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: theme.palette.warning.main,
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                    <TableContainer>
                                        <Table size={isMobile ? "small" : "medium"}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Soru No</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Kitapçık</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Konu</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Durum</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {group.questions
                                                    .sort((a, b) => a.soruNo - b.soruNo)
                                                    .map((question, qIndex) => {
                                                        // Find the matching test and check the answer
                                                        const result = isQuestionCorrect(group.testAdi, question.soruNo);

                                                        return (
                                                            <StyledTableRow key={qIndex}>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={question.soruNo}
                                                                        color={
                                                                            result === true ? "success" :
                                                                                result === false ? "error" :
                                                                                    "default"
                                                                        }
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{question.kitapcik}</TableCell>
                                                                <TableCell>{question.konu}</TableCell>
                                                                <TableCell align="center">
                                                                    {result === 'cancelled' && (
                                                                        <Tooltip title="İptal Edildi - Tüm Cevaplar Doğru" arrow>
                                                                            <Chip
                                                                                label="İptal"
                                                                                size="small"
                                                                                color="info"
                                                                                variant="outlined"
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                    {result === true && (
                                                                        <Tooltip title="Doğru" arrow>
                                                                            <CheckCircleIcon color="success" />
                                                                        </Tooltip>
                                                                    )}
                                                                    {result === false && (
                                                                        <Tooltip title="Yanlış" arrow>
                                                                            <CancelIcon color="error" />
                                                                        </Tooltip>
                                                                    )}
                                                                    {result === null && (
                                                                        <Tooltip title="Cevap Yok" arrow>
                                                                            <Chip
                                                                                label="Boş"
                                                                                size="small"
                                                                                color="warning"
                                                                                variant="outlined"
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                            </StyledTableRow>
                                                        );
                                                    })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            );
                        })}
                    </TabPanel>
                )}

                {/* Değerlendirme Tab - AI Analysis */}
                <TabPanel value={tabValue} index={sinavDetay.sinavSoruKazanimList?.length > 0 ? 4 : 3}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Yapay Zeka Değerlendirmesi
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Yapay zeka, sınav sonuçlarınızı analiz ederek kişiselleştirilmiş değerlendirme sunar.
                        </Typography>
                    </Box>
                    <Card
                        elevation={3}
                        sx={{
                            borderRadius: theme.shape.borderRadius * 1.5,
                            overflow: 'hidden'
                        }}
                    >
                        <CardHeader
                            avatar={
                                <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                                    <PsychologyIcon />
                                </Avatar>
                            }
                            title="Sınav Değerlendirmesi"
                            titleTypographyProps={{ variant: 'h6' }}
                            sx={{
                                bgcolor: theme.palette.info.main,
                                color: 'white',
                            }}
                        />
                        <CardContent sx={{ p: 3 }}>
                            {!hasRequestedAnalysis ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" paragraph>
                                        Yapay zeka değerlendirmesini görmek için analiz başlatın.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={requestAiAnalysis}
                                        startIcon={<PsychologyIcon />}
                                    >
                                        Analiz Başlat
                                    </Button>
                                </Box>
                            ) : isLoadingAnalysis ? (
                                <Box sx={{ textAlign: 'center', py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress size={60} />
                                    <Typography variant="h6" color="text.secondary">
                                        Sınav sonuçlarınız analiz ediliyor...
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Bu işlem birkaç saniye sürebilir.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    {aiAnalysis ? (
                                        <Box sx={{
                                            '& p': { mb: 2 },
                                            '& strong, & b': { fontWeight: 'bold', color: theme.palette.primary.main },
                                            '& em, & i': { fontStyle: 'italic', color: theme.palette.secondary.main },
                                            '& h1, & h2, & h3, & h4': {
                                                color: theme.palette.primary.dark,
                                                mt: 3,
                                                mb: 2
                                            },
                                            '& ul, & ol': {
                                                pl: 3,
                                                mb: 2,
                                                '& li': { mb: 1 }
                                            },
                                            lineHeight: 1.7
                                        }}>
                                            <ReactMarkdown>
                                                {aiAnalysis}
                                            </ReactMarkdown>
                                        </Box>
                                    ) : (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body1" color="error">
                                                Analiz sonuçları alınamadı. Lütfen tekrar deneyin.
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                sx={{ mt: 2 }}
                                                onClick={requestAiAnalysis}
                                            >
                                                Tekrar Dene
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </TabPanel>
            </DialogContent>
        </Dialog>
    );
};